import { useQueryClient } from "@tanstack/react-query";
import { FormattedMessage } from "react-intl";
import {
  getGetTemplateListQueryKey,
  getGetTemplateQueryKey,
  useGetTemplate,
  useUpdateTemplate,
} from "~/api/custom-reports/custom-reports";
import { Loader } from "~/components/Loader";
import { Button } from "~/components/ui/button";
import { Modal } from "~/components/ui/modal";
import { useToast } from "~/hooks/use-toast";

interface ViewTemplateModalProps {
  templateId: string;
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: () => void;
}

export function ViewTemplateModal({
  templateId,
  organizationId,
  open,
  onOpenChange,
  onEdit,
}: ViewTemplateModalProps) {
  const queryClient = useQueryClient();
  const { toast, dismiss } = useToast();

  const { data: template, isLoading } = useGetTemplate(
    organizationId,
    templateId,
    {
      query: {
        enabled: open,
      },
    }
  );

  const updateTemplate = useUpdateTemplate({
    mutation: {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Template approved successfully",
        });
        // Invalidate template list
        queryClient.invalidateQueries({
          queryKey: getGetTemplateListQueryKey(organizationId),
        });
        // Invalidate current template to reload it with updated data
        queryClient.invalidateQueries({
          queryKey: getGetTemplateQueryKey(organizationId, templateId),
        });
      },
      onError: () => {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to approve template",
        });
      },
    },
  });

  const handleEdit = () => {
    dismiss();
    onOpenChange(false);
    onEdit?.();
  };

  const handleClose = () => {
    dismiss();
    onOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      dismiss();
    }
    onOpenChange(open);
  };

  const handleApprove = () => {
    if (!template) return;

    updateTemplate.mutate({
      organizationId,
      templateId,
      data: {
        isDraft: false,
      },
    });
  };

  if (isLoading) {
    return (
      <Modal
        open={open}
        onOpenChange={handleOpenChange}
        title={<FormattedMessage id="loading" defaultMessage="Loading..." />}
        description=""
        content={
          <div className="flex justify-center items-center py-8">
            <Loader />
          </div>
        }
      />
    );
  }

  if (!template) {
    return null;
  }

  const sortedSections = [...(template.sections || [])].sort(
    (a, b) => a.order - b.order
  );

  const isFinishedDraft = template.isDraft && template.status === "completed";

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title={
        <div className="text-lg">
          <FormattedMessage
            id="viewTemplate.title"
            defaultMessage="{name}"
            values={{ name: template.name }}
          />
        </div>
      }
      description={
        <p className="text-muted-foreground text-sm">{template.definition}</p>
      }
      content={
        <div className="grid gap-4">
          {sortedSections.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-medium">
                <FormattedMessage
                  id="viewTemplate.sections"
                  defaultMessage="Sections"
                />
              </h3>
              <div className="space-y-3">
                {sortedSections.map((section, index) => (
                  <div
                    key={section.id || index}
                    className="border rounded-lg p-3 space-y-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-muted-foreground shrink-0">
                        {index + 1}.
                      </span>
                      <h4 className="font-medium min-w-0">{section.title}</h4>
                    </div>
                    {section.contextType !== "docList" &&
                      section.contextType !== "chronology" && (
                        <>
                          {section.purpose && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">
                                <FormattedMessage
                                  id="viewTemplate.purpose"
                                  defaultMessage="Purpose"
                                />
                              </p>
                              <p className="text-sm text-muted-foreground whitespace-pre-line">
                                {section.purpose}
                              </p>
                            </div>
                          )}
                          {section.prompt && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">
                                <FormattedMessage
                                  id="viewTemplate.prompt"
                                  defaultMessage="Prompt"
                                />
                              </p>
                              <p className="text-sm text-muted-foreground whitespace-pre-line">
                                {section.prompt}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    {section.contextType === "docList" && (
                      <p className="text-sm text-muted-foreground">
                        <FormattedMessage
                          id="viewTemplate.docListSection"
                          defaultMessage="Document list section"
                        />
                      </p>
                    )}
                    {section.contextType === "chronology" && (
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">
                          <FormattedMessage
                            id="viewTemplate.chronologySection"
                            defaultMessage="Chronology section"
                          />
                        </p>
                        {(() => {
                          const cfg = (
                            section as { config?: { prompt?: string } }
                          ).config;
                          return cfg?.prompt ? (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">
                                <FormattedMessage
                                  id="template.chronology.expansionPrompt"
                                  defaultMessage="Per-Event Expansion Prompt"
                                />
                              </p>
                              <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                                {cfg.prompt}
                              </p>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      }
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={handleEdit}
            className="sm:mr-auto"
          >
            <FormattedMessage id="edit" />
          </Button>
          <Button type="button" variant="outline" onClick={handleClose}>
            <FormattedMessage id="close" />
          </Button>
          {isFinishedDraft && (
            <Button
              type="button"
              onClick={handleApprove}
              disabled={updateTemplate.isPending}
            >
              <FormattedMessage id="approve" defaultMessage="Approve" />
            </Button>
          )}
        </>
      }
    />
  );
}
